import { useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DragTypes } from './';
import { PreviewCard } from './';
import type { XYCoord } from 'dnd-core';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/system/createTheme/createTheme';
import ControlKind from '../Shared/controlKind';
import DragHandleIcon from '@mui/icons-material/DragHandle';

interface PageProps {
  id: string;
  name: string;
}

interface DragPage {
  id: string;
  index: number;
}

const controlWrapperStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.grey[100],
  cursor: 'pointer',
  padding: '5px 0px 5px 15px'
});

const isMovingUpOrDown = (
  dragIndex: number,
  hoverIndex: number,
  hoverClientY: number,
  hoverMiddleY: number
) => {
  if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return true;

  if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return true;

  return false;
};

export default ({
  pageProps,
  index,
  moveCard
}: {
  pageProps: PageProps;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
}) => {
  const { id, name } = pageProps;

  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DragTypes.PAGE_LIST_ITEM,
      item: { id, index },
      canDrag: true,
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [id, index]
  );

  const [{ handlerId }, drop] = useDrop(
    () => ({
      accept: DragTypes.PAGE_LIST_ITEM,
      hover(item: DragPage, monitor) {
        if (!ref.current) {
          return;
        }

        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) {
          return;
        }

        const hoverBoundingRect = ref.current?.getBoundingClientRect();

        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        const clientOffset = monitor.getClientOffset();

        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (isMovingUpOrDown(dragIndex, hoverIndex, hoverClientY, hoverMiddleY))
          return;

        moveCard(dragIndex, hoverIndex);

        item.index = hoverIndex;
      },
      collect: (monitor) => ({
        handlerId: monitor.getHandlerId()
      })
    }),
    [index]
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  drag(drop(ref));

  return (
    <>
      {isDragging ? (
        PreviewCard(ControlKind.PAGE)
      ) : (
        <Stack
          ref={ref}
          data-handler-id={handlerId}
          sx={(theme) => ({
            ...controlWrapperStyles(theme)
          })}
          direction="row"
          alignItems="center"
          spacing={2}
        >
          <DragHandleIcon color="action" />
          <h4>{name}</h4>
        </Stack>
      )}
    </>
  );
};
